import clsx from 'clsx';
import React, { HTMLProps } from 'react';
import { tv, VariantProps } from 'tailwind-variants';
import { useTranslationDictionary } from '@/lib/i18n/useTranslationDictionary';

type BaseTagProps = HTMLProps<HTMLDivElement> &
  VariantProps<typeof tagStyle> & {
    text: string;
    icon?: React.ComponentType;
    iconClassName?: string;
    iconProps?: React.SVGProps<SVGSVGElement>;
  };

const tagStyle = tv({
  base: 'flex max-w-full items-center gap-x-1 rounded-tr-md border-2 text-sm font-semibold',
  variants: {
    clickable: {
      true: 'cursor-pointer hover:underline',
    },
    hasIcon: {
      true: 'pl-1 pr-1.5',
      false: 'px-1',
    },
    variant: {
      theme: 'border-rw-sub bg-rw-sub text-white',
      metadata: 'border-rw-green text-rw-green',
      topic: 'border-rw-black text-rw-black',
      topicSocialIssue: 'border-rw-black text-rw-red',
      mention: 'border-rw-gray text-rw-gray',
    },
  },
  defaultVariants: {
    variant: 'metadata',
  },
});

const BaseTag = ({
  text,
  className,
  icon: IconComponent,
  iconClassName,
  iconProps = {},
  variant,
  ...props
}: BaseTagProps) => {
  return (
    <div
      className={tagStyle({
        clickable: !!props.onClick,
        hasIcon: !!IconComponent,
        variant,
        className,
      })}
      {...props}
    >
      {IconComponent && (
        // flex-1 を設定しにないと見切れる場合にアイコンのサイズが小さくなる
        <span className="flex-1">
          <IconComponent
            className={clsx('size-4 stroke-2', iconClassName)}
            {...iconProps}
          />
        </span>
      )}
      <span className="truncate">{text}</span>
    </div>
  );
};

// テーマ用タグ（緑背景）
export const ThemeTag = ({
  tableName,
  iconClassName,
  ...props
}: Omit<BaseTagProps, 'text'> & { tableName: string }) => {
  const { getLabel } = useTranslationDictionary('theme');
  const text = getLabel(tableName);

  return (
    <BaseTag
      {...props}
      text={text}
      variant="theme"
      iconClassName={clsx('text-white', iconClassName)}
    />
  );
};

// メタデータ用タグ（国・組織タイプ・組織名）
export const MetadataTag = ({ iconClassName, ...props }: BaseTagProps) => (
  <BaseTag
    {...props}
    variant="metadata"
    iconClassName={clsx('text-rw-green', iconClassName)}
  />
);

// トピックタグ用（isSocialIssue対応）
export const TopicTag = ({
  isSocialIssue,
  iconClassName,
  ...props
}: BaseTagProps & { isSocialIssue?: boolean }) => (
  <BaseTag
    {...props}
    variant={isSocialIssue ? 'topicSocialIssue' : 'topic'}
    iconClassName={clsx(
      isSocialIssue ? 'text-rw-red' : 'text-rw-black',
      iconClassName
    )}
  />
);

// 言及用タグ（人物・企業・地名）
export const MentionTag = ({ iconClassName, ...props }: BaseTagProps) => (
  <BaseTag
    {...props}
    variant="mention"
    iconClassName={clsx('text-rw-gray', iconClassName)}
  />
);

export const TagContainer = ({
  title,
  children,
}: {
  title?: string;
  children: React.ReactNode;
}) => (
  <div>
    {title && <h5 className="mb-1 text-sm">{title}</h5>}
    <div className="flex flex-wrap gap-1">{children}</div>
  </div>
);
